/**
 * Role Details page - View and manage role settings
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { roleAPI, Role } from '../api/client';
import TopRibbon from '../components/TopRibbon';

const RoleDetails: React.FC = () => {
  const { roleId } = useParams<{ roleId: string }>();
  const navigate = useNavigate();

  const [role, setRole] = useState<Role | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchRole = React.useCallback(async () => {
    if (!roleId) return;
    try {
      const response = await roleAPI.getRole(roleId);
      setRole(response.data);
    } catch (error) {
      console.error('Failed to fetch role:', error);
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => {
    if (roleId) {
      fetchRole();
    }
  }, [roleId, fetchRole]);

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading role details...</div>;
  }

  if (!role) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Role not found</div>;
  }

  // Simple markdown-like rendering
  const renderCriteria = (text: string) => {
    if (!text) return null;

    return text.split('\n').map((line, i) => {
      const trimmedLine = line.trim();

      // Empty lines
      if (trimmedLine === '') {
        return <div key={i} style={{ height: '8px' }} />;
      }

      // Headers (all caps lines without ** are treated as headers)
      if (trimmedLine === trimmedLine.toUpperCase() && trimmedLine.length > 10 && !trimmedLine.includes('**')) {
        return (
          <h3 key={i} style={{
            margin: '24px 0 12px 0',
            fontSize: '18px',
            fontWeight: 700,
            color: '#1a1a1a',
            textAlign: 'left'
          }}>
            {trimmedLine}
          </h3>
        );
      }

      // Bullet points
      if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
        const content = trimmedLine.substring(2);
        return (
          <li key={i} style={{
            marginLeft: '20px',
            marginBottom: '8px',
            color: '#374151',
            lineHeight: '1.6',
            textAlign: 'left'
          }}>
            {renderLineWithBold(content)}
          </li>
        );
      }

      // Regular text (may contain bold)
      return (
        <p key={i} style={{
          margin: '8px 0',
          color: '#374151',
          lineHeight: '1.6',
          textAlign: 'left'
        }}>
          {renderLineWithBold(trimmedLine)}
        </p>
      );
    });
  };

  // Helper to render text with **bold** markdown
  const renderLineWithBold = (text: string) => {
    if (!text.includes('**')) {
      return text;
    }

    const parts = text.split('**');
    return (
      <>
        {parts.map((part, j) =>
          j % 2 === 0 ? part : <strong key={j} style={{ fontWeight: 700 }}>{part}</strong>
        )}
      </>
    );
  };

  return (
    <>
      <TopRibbon />
      <div style={{ minHeight: '100vh', background: '#f8f9fa', paddingTop: '64px' }}>
        <div style={{ padding: '40px', maxWidth: '900px', margin: '0 auto' }}>
          <button
            onClick={() => navigate('/roles')}
            style={{
              marginBottom: '32px',
              padding: '10px 20px',
              background: 'white',
              color: '#2d5016',
              border: '2px solid #2d5016',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#2d5016';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.color = '#2d5016';
            }}
          >
            ← Back to Roles
          </button>

          <h1 style={{ margin: '0 0 8px 0', fontSize: '32px', fontWeight: 700, color: '#1a1a1a' }}>
            {role.title}
          </h1>
          {role.description && (
            <p style={{ margin: '0 0 24px 0', fontSize: '16px', color: '#6b7280' }}>
              {role.description}
            </p>
          )}

          <div style={{ marginTop: '32px' }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '24px', fontWeight: 700, color: '#1a1a1a' }}>
              Criteria
            </h2>
            <div style={{
              padding: '24px',
              background: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              textAlign: 'left'
            }}>
              {renderCriteria(role.criteria || '')}
            </div>
            {role.is_criteria_locked && (
              <p style={{ marginTop: '12px', color: '#92400e', fontWeight: 600 }}>
                🔒 Criteria are locked (cannot be modified)
              </p>
            )}
          </div>

          <div style={{ marginTop: '32px', display: 'flex', gap: '12px' }}>
            <button
              onClick={() => navigate(`/roles/${roleId}/candidates`)}
              style={{
                padding: '12px 24px',
                background: '#2d5016',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#3d6b1f'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#2d5016'}
            >
              View Candidates
            </button>
            <button
              onClick={() => navigate(`/roles/${roleId}/upload`)}
              style={{
                padding: '12px 24px',
                background: 'white',
                color: '#2d5016',
                border: '2px solid #2d5016',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#2d5016';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#2d5016';
              }}
            >
              Upload Binder
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default RoleDetails;
